/**
 * Expense Tracking Tab
 * 
 * Handles expense entry, categorization, and Profit & Loss reporting.
 * Categories: COGS (Food/Beverage), Payroll, Overhead
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Receipt, Plus, Trash2, Edit, RefreshCw, Calendar,
  TrendingUp, TrendingDown, AlertTriangle, Check, Loader2,
  DollarSign, PieChart, BarChart3, ChevronDown, ChevronUp,
  Building, Coffee, Users, Zap, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Format currency with $ for admin
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

// Category icons
const CATEGORY_ICONS = {
  cogs: Coffee,
  payroll: Users,
  overhead: Building,
};

// Status colors for health indicators
const getHealthColor = (isHealthy) => isHealthy ? 'text-green-600' : 'text-red-600';
const getHealthBg = (isHealthy) => isHealthy ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30';

export default function ExpenseTrackingTab() {
  const [categories, setCategories] = useState({});
  const [expenses, setExpenses] = useState([]);
  const [pnlData, setPnlData] = useState(null);
  const [pnlComparison, setPnlComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [activeView, setActiveView] = useState('pnl'); // 'pnl', 'expenses'
  
  // Form state
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split('T')[0],
    category: 'cogs',
    subcategory: '',
    vendor_name: '',
    description: '',
    amount: '',
    payment_method: 'cash',
    reference_number: '',
    is_recurring: false,
    notes: ''
  });
  
  // Fetch expense categories
  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/expenses/categories');
      setCategories(res.data.categories || {});
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);
  
  // Fetch expenses
  const fetchExpenses = useCallback(async () => {
    try {
      const today = new Date();
      let startDate;
      
      if (selectedPeriod === 'day') {
        startDate = today.toISOString().split('T')[0];
      } else if (selectedPeriod === 'week') {
        const start = new Date(today);
        start.setDate(start.getDate() - start.getDay());
        startDate = start.toISOString().split('T')[0];
      } else if (selectedPeriod === 'month') {
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      } else if (selectedPeriod === 'quarter') {
        const quarterStart = Math.floor(today.getMonth() / 3) * 3;
        startDate = new Date(today.getFullYear(), quarterStart, 1).toISOString().split('T')[0];
      } else {
        startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
      }
      
      const res = await api.get(`/admin/finance/expenses?start_date=${startDate}&limit=200`);
      setExpenses(res.data.expenses || []);
    } catch (err) {
      console.error('Failed to fetch expenses:', err);
    }
  }, [selectedPeriod]);
  
  // Fetch P&L data
  const fetchPnL = useCallback(async () => {
    try {
      const res = await api.get(`/admin/finance/pnl?period=${selectedPeriod}`);
      setPnlData(res.data);
    } catch (err) {
      console.error('Failed to fetch P&L:', err);
    }
  }, [selectedPeriod]);
  
  // Fetch P&L comparison
  const fetchPnLComparison = useCallback(async () => {
    try {
      const res = await api.get(`/admin/finance/pnl/comparison?period=${selectedPeriod}`);
      setPnlComparison(res.data);
    } catch (err) {
      console.error('Failed to fetch P&L comparison:', err);
    }
  }, [selectedPeriod]);
  
  // Initial load
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);
  
  // Refresh when period changes
  useEffect(() => {
    fetchExpenses();
    fetchPnL();
    fetchPnLComparison();
  }, [selectedPeriod, fetchExpenses, fetchPnL, fetchPnLComparison]);
  
  // Add/Update expense
  const handleSaveExpense = async () => {
    if (!expenseForm.subcategory || !expenseForm.description || !expenseForm.amount) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      };
      
      if (editingExpense) {
        await api.put(`/admin/finance/expenses/${editingExpense.id}`, payload);
      } else {
        await api.post('/admin/finance/expenses', payload);
      }
      
      toast.success(editingExpense ? 'Expense updated' : 'Expense added');
      setShowAddDialog(false);
      setEditingExpense(null);
      resetForm();
      fetchExpenses();
      fetchPnL();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save expense');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete expense
  const handleDeleteExpense = async (id) => {
    if (!confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      await api.delete(`/admin/finance/expenses/${id}`);
      toast.success('Expense deleted');
      fetchExpenses();
      fetchPnL();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete expense');
    }
  };
  
  // Reset form
  const resetForm = () => {
    setExpenseForm({
      date: new Date().toISOString().split('T')[0],
      category: 'cogs',
      subcategory: '',
      vendor_name: '',
      description: '',
      amount: '',
      payment_method: 'cash',
      reference_number: '',
      is_recurring: false,
      notes: ''
    });
  };
  
  // Edit expense
  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      date: expense.date,
      category: expense.category,
      subcategory: expense.subcategory,
      vendor_name: expense.vendor_name || '',
      description: expense.description,
      amount: expense.amount.toString(),
      payment_method: expense.payment_method || 'cash',
      reference_number: expense.reference_number || '',
      is_recurring: expense.is_recurring || false,
      notes: expense.notes || ''
    });
    setShowAddDialog(true);
  };
  
  // Refresh all
  const handleRefresh = () => {
    fetchExpenses();
    fetchPnL();
    fetchPnLComparison();
  };
  
  // Get subcategories for selected category
  const getSubcategories = () => {
    const cat = categories[expenseForm.category];
    return cat?.subcategories || [];
  };
  
  // Group expenses by category
  const expensesByCategory = expenses.reduce((acc, exp) => {
    if (!acc[exp.category]) acc[exp.category] = [];
    acc[exp.category].push(exp);
    return acc;
  }, {});
  
  return (
    <div className="space-y-6" data-testid="expense-tracking-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Expense Tracking & P/L</h2>
          <p className="text-muted-foreground">
            Track expenses and view profit/loss statements
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => { resetForm(); setShowAddDialog(true); }} data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>
      
      {/* View Toggle */}
      <div className="flex gap-2 p-1 bg-muted rounded-lg w-fit">
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeView === 'pnl' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
          }`}
          onClick={() => setActiveView('pnl')}
        >
          <PieChart className="w-4 h-4 inline mr-2" />
          Profit & Loss
        </button>
        <button
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeView === 'expenses' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
          }`}
          onClick={() => setActiveView('expenses')}
        >
          <Receipt className="w-4 h-4 inline mr-2" />
          Expenses
        </button>
      </div>
      
      {/* P&L View */}
      {activeView === 'pnl' && pnlData && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Revenue</p>
                    <p className="text-xl font-bold text-foreground">{formatCurrency(pnlData.revenue?.total_sales)} </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getHealthBg(pnlData.health_indicators?.profitable)}`}>
                    {pnlData.net_profit?.amount >= 0 ? (
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Net Profit</p>
                    <p className={`text-xl font-bold ${pnlData.net_profit?.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {pnlData.net_profit?.amount >= 0 ? '' : '-'}{formatCurrency(Math.abs(pnlData.net_profit?.amount || 0))}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {pnlData.net_profit?.margin_percent || 0}% margin
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getHealthBg(pnlData.health_indicators?.food_cost_healthy)}`}>
                    <Coffee className={`w-5 h-5 ${getHealthColor(pnlData.health_indicators?.food_cost_healthy)}`} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">COGS</p>
                    <p className="text-xl font-bold text-foreground">{formatCurrency(pnlData.cost_of_goods_sold?.total)} </p>
                    <p className={`text-xs ${getHealthColor(pnlData.health_indicators?.food_cost_healthy)}`}>
                      {pnlData.cost_of_goods_sold?.percent_of_sales || 0}% of sales
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getHealthBg(pnlData.health_indicators?.prime_cost_healthy)}`}>
                    <BarChart3 className={`w-5 h-5 ${getHealthColor(pnlData.health_indicators?.prime_cost_healthy)}`} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Prime Cost</p>
                    <p className="text-xl font-bold text-foreground">{formatCurrency(pnlData.prime_cost?.amount)} </p>
                    <p className={`text-xs ${getHealthColor(pnlData.health_indicators?.prime_cost_healthy)}`}>
                      {pnlData.prime_cost?.percent_of_sales || 0}% (target: 60%)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Period Comparison */}
          {pnlComparison && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Period Comparison</CardTitle>
                <CardDescription>
                  {pnlComparison.current?.start} to {pnlComparison.current?.end} vs previous period
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Revenue Change</p>
                    <p className={`text-2xl font-bold ${pnlComparison.changes?.revenue_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {pnlComparison.changes?.revenue_change_percent >= 0 ? '+' : ''}{pnlComparison.changes?.revenue_change_percent || 0}%
                    </p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Expense Change</p>
                    <p className={`text-2xl font-bold ${pnlComparison.changes?.expense_change_percent <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {pnlComparison.changes?.expense_change_percent >= 0 ? '+' : ''}{pnlComparison.changes?.expense_change_percent || 0}%
                    </p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Profit Change</p>
                    <p className={`text-2xl font-bold ${pnlComparison.changes?.profit_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {pnlComparison.changes?.profit_change_percent >= 0 ? '+' : ''}{pnlComparison.changes?.profit_change_percent || 0}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Detailed P&L Statement */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Profit & Loss Statement
              </CardTitle>
              <CardDescription>
                {pnlData.period?.start_date} to {pnlData.period?.end_date}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Revenue */}
                <div className="border-b pb-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-foreground">Total Revenue</span>
                    <span className="font-bold text-foreground">{formatCurrency(pnlData.revenue?.total_sales)} </span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {pnlData.revenue?.order_count} orders
                  </div>
                </div>
                
                {/* COGS */}
                <div className="border-b pb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Less: Cost of Goods Sold</span>
                    <span className="text-red-600">-{formatCurrency(pnlData.cost_of_goods_sold?.total)} </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    ({pnlData.cost_of_goods_sold?.percent_of_sales}% of sales | Benchmark: {pnlData.cost_of_goods_sold?.benchmark?.min}-{pnlData.cost_of_goods_sold?.benchmark?.max}%)
                  </div>
                </div>
                
                {/* Gross Profit */}
                <div className="border-b pb-4 bg-muted/30 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-foreground">Gross Profit</span>
                    <span className="font-bold text-green-600">{formatCurrency(pnlData.gross_profit?.amount)} </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {pnlData.gross_profit?.margin_percent}% margin
                  </div>
                </div>
                
                {/* Payroll */}
                <div className="border-b pb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Less: Payroll</span>
                    <span className="text-red-600">-{formatCurrency(pnlData.payroll?.total)} </span>
                  </div>
                  <div className={`text-xs ${getHealthColor(pnlData.health_indicators?.labor_cost_healthy)}`}>
                    ({pnlData.payroll?.percent_of_sales}% of sales | Benchmark: {pnlData.payroll?.benchmark?.min}-{pnlData.payroll?.benchmark?.max}%)
                  </div>
                </div>
                
                {/* Overhead */}
                <div className="border-b pb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Less: Overhead</span>
                    <span className="text-red-600">-{formatCurrency(pnlData.overhead?.total)} </span>
                  </div>
                  <div className={`text-xs ${getHealthColor(pnlData.health_indicators?.overhead_healthy)}`}>
                    ({pnlData.overhead?.percent_of_sales}% of sales | Benchmark: {pnlData.overhead?.benchmark?.min}-{pnlData.overhead?.benchmark?.max}%)
                  </div>
                </div>
                
                {/* Net Profit */}
                <div className={`p-4 rounded-lg ${pnlData.net_profit?.amount >= 0 ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-foreground">Net {pnlData.net_profit?.status === 'profit' ? 'Profit' : 'Loss'}</span>
                    <span className={`text-2xl font-bold ${pnlData.net_profit?.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(Math.abs(pnlData.net_profit?.amount || 0))}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {pnlData.net_profit?.margin_percent}% net margin (Industry target: {pnlData.net_profit?.benchmark?.target}%)
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Health Indicators */}
          <Card>
            <CardHeader>
              <CardTitle>Health Check</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {[
                  { label: 'Food Cost', key: 'food_cost_healthy' },
                  { label: 'Labor Cost', key: 'labor_cost_healthy' },
                  { label: 'Overhead', key: 'overhead_healthy' },
                  { label: 'Prime Cost', key: 'prime_cost_healthy' },
                  { label: 'Profitable', key: 'profitable' },
                ].map(indicator => (
                  <div 
                    key={indicator.key}
                    className={`p-3 rounded-lg text-center ${getHealthBg(pnlData.health_indicators?.[indicator.key])}`}
                  >
                    {pnlData.health_indicators?.[indicator.key] ? (
                      <Check className={`w-5 h-5 mx-auto mb-1 ${getHealthColor(true)}`} />
                    ) : (
                      <AlertTriangle className={`w-5 h-5 mx-auto mb-1 ${getHealthColor(false)}`} />
                    )}
                    <p className="text-xs font-medium">{indicator.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Expenses View */}
      {activeView === 'expenses' && (
        <div className="space-y-6">
          {/* Expense Summary by Category */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {['cogs', 'payroll', 'overhead'].map(catId => {
              const cat = categories[catId];
              const catExpenses = expensesByCategory[catId] || [];
              const total = catExpenses.reduce((sum, e) => sum + e.amount, 0);
              const Icon = CATEGORY_ICONS[catId] || Receipt;
              
              return (
                <Card key={catId}>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{cat?.name || catId}</p>
                        <p className="text-xl font-bold text-foreground">{formatCurrency(total)} </p>
                        <p className="text-xs text-muted-foreground">{catExpenses.length} entries</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Expense List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Expense Entries
              </CardTitle>
              <CardDescription>
                {expenses.length} expenses for selected period
              </CardDescription>
            </CardHeader>
            <CardContent>
              {expenses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No expenses recorded for this period</p>
                  <Button 
                    className="mt-4" 
                    onClick={() => { resetForm(); setShowAddDialog(true); }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Expense
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Group by category */}
                  {['cogs', 'payroll', 'overhead'].map(catId => {
                    const catExpenses = expensesByCategory[catId] || [];
                    if (catExpenses.length === 0) return null;
                    
                    const cat = categories[catId];
                    const Icon = CATEGORY_ICONS[catId] || Receipt;
                    const isExpanded = expandedCategory === catId;
                    
                    return (
                      <div key={catId} className="border rounded-lg overflow-hidden">
                        <button
                          className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
                          onClick={() => setExpandedCategory(isExpanded ? null : catId)}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium text-foreground">{cat?.name || catId}</p>
                              <p className="text-sm text-muted-foreground">{catExpenses.length} entries</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="font-medium text-foreground">
                              {formatCurrency(catExpenses.reduce((sum, e) => sum + e.amount, 0))}
                            </span>
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </div>
                        </button>
                        
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <div className="divide-y">
                                {catExpenses.map(expense => (
                                  <div
                                    key={expense.id}
                                    className="flex items-center justify-between p-4 hover:bg-muted/20 transition-colors"
                                  >
                                    <div className="flex-1">
                                      <p className="font-medium text-foreground">{expense.description}</p>
                                      <p className="text-sm text-muted-foreground">
                                        {expense.date} {expense.vendor_name && `• ${expense.vendor_name}`}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="font-medium text-foreground">{formatCurrency(expense.amount)} </span>
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleEditExpense(expense)}
                                        >
                                          <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                          onClick={() => handleDeleteExpense(expense.id)}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Add/Edit Expense Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { 
        setShowAddDialog(open); 
        if (!open) { setEditingExpense(null); resetForm(); }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
            <DialogDescription>
              {editingExpense ? 'Update the expense details' : 'Enter the expense details'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Amount (FJD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select 
                  value={expenseForm.category} 
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v, subcategory: '' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([id, cat]) => (
                      <SelectItem key={id} value={id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subcategory *</Label>
                <Select 
                  value={expenseForm.subcategory} 
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, subcategory: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getSubcategories().map(sub => (
                      <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Input
                placeholder="What was this expense for?"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor (Optional)</Label>
                <Input
                  placeholder="Supplier name"
                  value={expenseForm.vendor_name}
                  onChange={(e) => setExpenseForm({ ...expenseForm, vendor_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select 
                  value={expenseForm.payment_method} 
                  onValueChange={(v) => setExpenseForm({ ...expenseForm, payment_method: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="transfer">Bank Transfer</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Reference Number (Optional)</Label>
              <Input
                placeholder="Invoice or receipt number"
                value={expenseForm.reference_number}
                onChange={(e) => setExpenseForm({ ...expenseForm, reference_number: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Any additional notes..."
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveExpense} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {editingExpense ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
